<template>
  <div class="h-full flex flex-col min-h-0">
    <TableToolbar
      :columns="COLUMNS"
      search-key="students"
      v-model:search-query="searchQuery"
      v-model:active-filters="activeFilters"
      v-model:visible-keys="visibleKeys"
      v-model:column-order="columnOrder"
      :is-loading="isLoading"
      search-placeholder="Search name, contact, email…"
      @refresh="fetchStudents"
      @reset-columns="resetColumns"
    >
      <template #count>
        {{ filteredStudents.length }} of {{ students.length }} student{{ students.length === 1 ? '' : 's' }}
      </template>
    </TableToolbar>

    <!-- Table -->
    <div class="flex-1 min-h-0 bg-surface-raised dark:bg-surface-raised-dark rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden">
      <div class="h-full overflow-auto">
        <table class="min-w-full divide-y divide-stroke dark:divide-stroke-dark">
          <thead class="bg-surface-muted dark:bg-gray-900">
            <tr>
              <th
                v-for="col in activeColumns"
                :key="col.key"
                @click="toggleSort(col.key)"
                class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group hover:bg-surface-muted dark:hover:bg-surface-muted-dark transition-colors"
                :class="sortKey === col.key ? 'text-blue-600 dark:text-blue-400' : 'text-fg-muted dark:text-fg-muted-dark'"
              >
                <div class="flex items-center gap-1">
                  {{ col.label }}
                  <svg
                    v-if="sortKey === col.key"
                    class="h-3 w-3 flex-shrink-0 transition-transform"
                    :class="sortDir === 'desc' ? 'rotate-180' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 15l7-7 7 7" />
                  </svg>
                  <svg
                    v-else
                    class="h-3 w-3 flex-shrink-0 text-stroke-strong dark:text-fg-faint-dark opacity-0 group-hover:opacity-100 transition-opacity"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-stroke dark:divide-stroke-dark">

            <!-- Loading -->
            <tr v-if="isLoading">
              <td :colspan="activeColumns.length" class="px-4 py-10 text-center">
                <svg class="animate-spin h-6 w-6 mx-auto text-fg-faint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </td>
            </tr>

            <!-- Empty -->
            <tr v-else-if="filteredStudents.length === 0">
              <td :colspan="activeColumns.length" class="px-4 py-10 text-center text-sm text-fg-muted dark:text-fg-muted-dark">
                {{ students.length === 0 ? 'No students found on your calendar' : 'No students match your filters' }}
              </td>
            </tr>

            <!-- Data rows -->
            <tr
              v-else
              v-for="row in filteredStudents"
              :key="row.id"
              class="hover:bg-surface-muted dark:hover:bg-gray-900 transition-colors"
            >
              <template v-for="col in activeColumns" :key="col.key">
                <td :class="['px-4 py-3 text-sm text-fg-secondary dark:text-fg-secondary-dark', col.tdClass]">

                  <!-- Name -->
                  <template v-if="col.key === 'name'">
                    <span class="font-medium text-fg-base dark:text-fg-base-dark">{{ row.name || '—' }}</span>
                  </template>

                  <!-- Status badge -->
                  <template v-else-if="col.key === 'status'">
                    <span v-if="row.status" :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', statusClass(row.status)]">
                      {{ row.status }}
                    </span>
                    <span v-else class="text-fg-faint dark:text-fg-faint-dark">—</span>
                  </template>

                  <!-- DOB -->
                  <template v-else-if="col.key === 'dob'">
                    <span class="text-xs text-fg-muted dark:text-fg-muted-dark">{{ row.dob || '—' }}</span>
                  </template>

                  <!-- Contact name (+ additional contacts popover) -->
                  <template v-else-if="col.key === 'contact_name'">
                    <span class="relative inline-flex items-center gap-1">
                      <span v-if="row.contact_name" class="text-fg-secondary dark:text-fg-secondary-dark">{{ row.contact_name }}</span>
                      <span v-else class="text-fg-faint dark:text-fg-faint-dark">—</span>
                      <button
                        v-if="row.recipients?.length"
                        @click.stop="openRecipientsFor = openRecipientsFor === row.id ? null : row.id"
                        class="inline-flex items-center justify-center h-4 min-w-[1.1rem] px-1 text-[10px] font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        :title="`${row.recipients.length} additional contact${row.recipients.length === 1 ? '' : 's'}`"
                      >+{{ row.recipients.length }}</button>

                      <!-- Additional contacts popover -->
                      <div
                        v-if="openRecipientsFor === row.id"
                        class="absolute left-0 top-full mt-1 w-64 rounded-lg bg-surface dark:bg-surface-dark shadow-lg ring-1 ring-black/5 dark:ring-stroke-dark z-20 p-2 space-y-2"
                        @click.stop
                      >
                        <div
                          v-for="r in row.recipients"
                          :key="r.id"
                          class="text-xs border-b border-stroke-subtle dark:border-stroke-dark last:border-0 pb-2 last:pb-0"
                        >
                          <div class="flex items-center gap-1.5 mb-0.5">
                            <span class="font-medium text-fg-base dark:text-fg-base-dark">{{ r.name }}</span>
                            <span v-if="r.label" class="px-1.5 py-0.5 rounded-full bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark">{{ r.label }}</span>
                          </div>
                          <a v-if="r.email" :href="`mailto:${r.email}`" class="block text-blue-600 dark:text-blue-400 hover:underline">{{ r.email }}</a>
                          <a v-if="r.phone" :href="`tel:${r.phone}`" class="block text-fg-secondary dark:text-fg-secondary-dark hover:underline">{{ fmtPhone(r.phone) }}</a>
                        </div>
                      </div>
                    </span>
                  </template>

                  <!-- Contact email -->
                  <template v-else-if="col.key === 'contact_email'">
                    <a v-if="row.contact_email" :href="`mailto:${row.contact_email}`" class="text-blue-600 dark:text-blue-400 hover:underline">{{ row.contact_email }}</a>
                    <span v-else class="text-fg-faint dark:text-fg-faint-dark">—</span>
                  </template>

                  <!-- Contact phone -->
                  <template v-else-if="col.key === 'contact_phone'">
                    <a v-if="row.contact_phone" :href="`tel:${row.contact_phone}`" class="hover:underline">{{ fmtPhone(row.contact_phone) }}</a>
                    <span v-else class="text-fg-faint dark:text-fg-faint-dark">—</span>
                  </template>

                  <!-- Preferred contact method -->
                  <template v-else-if="col.key === 'preferred_contact_method'">
                    <select
                      :value="row.preferred_contact_method || 'either'"
                      @change="e => updateContactPreference(row, e.target.value)"
                      @click.stop
                      class="text-xs rounded border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark px-1.5 py-0.5 text-fg-secondary dark:text-fg-secondary-dark"
                    >
                      <option value="either">Either</option>
                      <option value="text">Text</option>
                      <option value="email">Email</option>
                    </select>
                  </template>

                  <!-- Payment method link -->
                  <template v-else-if="col.key === 'payment_link'">
                    <div v-if="row.contact_id" class="inline-flex rounded-md shadow-sm">
                      <button
                        @click.stop="copySetupLink(row)"
                        :disabled="setupLinkLoading === row.contact_id"
                        title="Copy a payment-method-update link to the clipboard"
                        class="px-2 py-1 text-xs border rounded-l-md transition-colors"
                        :class="copiedSetupLinks.has(row.contact_id)
                          ? 'border-green-500 dark:border-green-600 text-green-600 dark:text-green-400'
                          : 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'"
                      >{{ copiedSetupLinks.has(row.contact_id) ? 'Copied!' : setupLinkLoading === row.contact_id ? '…' : 'Copy' }}</button>
                      <button
                        @click.stop="emailSetupLink(row)"
                        :disabled="emailSetupLoading === row.contact_id || !row.contact_email"
                        :title="row.contact_email ? 'Email a payment-method-update link' : 'No email on file for this contact'"
                        class="px-2 py-1 text-xs border border-l-0 rounded-r-md transition-colors disabled:opacity-40"
                        :class="emailedSetupLinks.has(row.contact_id)
                          ? 'border-green-500 dark:border-green-600 text-green-600 dark:text-green-400'
                          : 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'"
                      >{{ emailedSetupLinks.has(row.contact_id) ? 'Sent!' : emailSetupLoading === row.contact_id ? '…' : 'Email' }}</button>
                    </div>
                    <span v-else class="text-fg-faint dark:text-fg-faint-dark">—</span>
                  </template>

                  <!-- Generic fallback -->
                  <template v-else>
                    {{ row[col.key] || '—' }}
                  </template>

                </td>
              </template>
            </tr>

          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useApi } from '../../composables/useApi.js'
import { useTableControls } from '../../composables/useTableControls.js'
import { useDemoMode } from '../../composables/useDemoMode.js'
import TableToolbar from '../common/TableToolbar.vue'

const { fetchApi } = useApi()
const { viewAsTeacher } = useDemoMode()

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtPhone = raw => {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`
  if (digits.length === 11 && digits[0] === '1') return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`
  return raw
}

const statusClass = status => ({
  active:   'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  former:   'bg-surface-muted text-fg-secondary dark:bg-surface-muted-dark dark:text-fg-secondary-dark',
  inactive: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
}[status] || 'bg-surface-muted text-fg-secondary dark:bg-surface-muted-dark dark:text-fg-secondary-dark')

const openRecipientsFor = ref(null)

// ── Click-outside to close the additional-contacts popover ────────────────────
const handleOutsideClick = () => { openRecipientsFor.value = null }
onMounted(() => document.addEventListener('click', handleOutsideClick))
onUnmounted(() => document.removeEventListener('click', handleOutsideClick))

// ── Column definitions ────────────────────────────────────────────────────────
const COLUMNS = [
  {
    key: 'name', label: 'Name', default: true,
    sortValue: row => (row.name || '').toLowerCase(),
  },
  {
    key: 'status', label: 'Status', default: false,
    ops: ['is', 'is not', 'is empty', 'is not empty'],
    valueOptions: ['active', 'former', 'inactive'],
  },
  {
    key: 'dob', label: 'Date of Birth', default: false, tdClass: 'whitespace-nowrap',
  },
  {
    key: 'contact_name', label: 'Contact', default: true,
    sortValue: row => (row.contact_name || '').toLowerCase(),
  },
  {
    key: 'contact_phone', label: 'Contact Phone', default: true, tdClass: 'whitespace-nowrap',
    filterValue: row => row.contact_phone ? row.contact_phone.replace(/\D/g, '') : '',
  },
  {
    key: 'contact_email', label: 'Contact Email', default: true,
  },
  {
    key: 'preferred_contact_method', label: 'Prefers', default: true, tdClass: 'whitespace-nowrap',
    ops: ['is', 'is not', 'is empty', 'is not empty'],
    valueOptions: ['either', 'text', 'email'],
  },
  {
    key: 'payment_link', label: 'Payment', default: true, tdClass: 'whitespace-nowrap',
  },
]

// ── Table controls ────────────────────────────────────────────────────────────
const {
  columnOrder, visibleKeys, activeColumns,
  resetColumns,
  sortKey, sortDir, toggleSort,
  searchQuery, activeFilters,
  filterRows,
} = useTableControls(
  COLUMNS,
  'teacher_students_columns_v2',
  (row, q) => {
    const digits = q.replace(/\D/g, '')
    return (
      row.name?.toLowerCase().includes(q)          ||
      row.contact_name?.toLowerCase().includes(q)  ||
      row.contact_email?.toLowerCase().includes(q) ||
      (digits.length > 0 && (row.contact_phone || '').replace(/\D/g, '').includes(digits))
    )
  }
)

// ── Data ──────────────────────────────────────────────────────────────────────
const students  = ref([])
const isLoading = ref(false)

const filteredStudents = computed(() => filterRows(students.value))

const fetchStudents = async () => {
  if (isLoading.value) return
  isLoading.value = true
  try {
    const qs = viewAsTeacher.value ? `?as_teacher=${encodeURIComponent(viewAsTeacher.value.name)}` : ''
    students.value = await fetchApi(`/api/students/roster${qs}`)
  } catch {
    console.error('Failed to fetch students')
  } finally {
    isLoading.value = false
  }
}

const updateContactPreference = async (row, value) => {
  const previous = row.preferred_contact_method
  row.preferred_contact_method = value
  try {
    await fetchApi(`/api/students/${row.id}/contact-preference`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preferred_contact_method: value }),
    })
  } catch {
    row.preferred_contact_method = previous
    console.error('Failed to update contact preference')
  }
}

onMounted(() => fetchStudents())

// ── Payment setup link ────────────────────────────────────────────────────────
const copiedSetupLinks  = ref(new Set())
const setupLinkLoading  = ref(null)
const emailedSetupLinks = ref(new Set())
const emailSetupLoading = ref(null)

async function copySetupLink(row) {
  setupLinkLoading.value = row.contact_id
  try {
    const data = await fetchApi(`/api/billing/setup/link/${row.contact_id}`, { method: 'POST' })
    await navigator.clipboard.writeText(data.url)
    const s = new Set(copiedSetupLinks.value)
    s.add(row.contact_id)
    copiedSetupLinks.value = s
    setTimeout(() => {
      const s2 = new Set(copiedSetupLinks.value)
      s2.delete(row.contact_id)
      copiedSetupLinks.value = s2
    }, 1500)
  } finally {
    setupLinkLoading.value = null
  }
}

async function emailSetupLink(row) {
  emailSetupLoading.value = row.contact_id
  try {
    await fetchApi(`/api/billing/setup/link/${row.contact_id}?send_email=true`, { method: 'POST' })
    const s = new Set(emailedSetupLinks.value)
    s.add(row.contact_id)
    emailedSetupLinks.value = s
    setTimeout(() => {
      const s2 = new Set(emailedSetupLinks.value)
      s2.delete(row.contact_id)
      emailedSetupLinks.value = s2
    }, 1500)
  } finally {
    emailSetupLoading.value = null
  }
}
</script>
